import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

/* ─── Icono por tipo de archivo ─── */
const FILE_ICONS = {
  image: "mdi:file-image-outline",
  pdf: "mdi:file-pdf-box",
  word: "mdi:file-word-outline",
  excel: "mdi:file-excel-outline",
  ppt: "mdi:file-powerpoint-outline",
  video: "mdi:file-video-outline",
  audio: "mdi:file-music-outline",
  zip: "mdi:folder-zip-outline",
  default: "mdi:file-document-outline",
};

const getFileIcon = (fileName) => {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();
  if (!ext) return FILE_ICONS.default;
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext)) return FILE_ICONS.image;
  if (ext === "pdf") return FILE_ICONS.pdf;
  if (["doc", "docx"].includes(ext)) return FILE_ICONS.word;
  if (["xls", "xlsx", "csv"].includes(ext)) return FILE_ICONS.excel;
  if (["ppt", "pptx"].includes(ext)) return FILE_ICONS.ppt;
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return FILE_ICONS.video;
  if (["mp3", "wav", "ogg", "flac", "aac", "m4a"].includes(ext)) return FILE_ICONS.audio;
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return FILE_ICONS.zip;
  return FILE_ICONS.default;
};

const RevisionModal = ({
  show,
  onHide,
  onSubmit,
  submitting = false,
  actividad,
}) => {
  const [comentario, setComentario] = useState("");

  React.useEffect(() => {
    if (show) setComentario("");
  }, [show]);

  const handleSubmit = (accion) => (e) => {
    e.preventDefault();
    const payload = { accion };
    if (comentario.trim()) payload.comentario_docente = comentario.trim();
    onSubmit(payload);
  };

  if (!show) return null;

  // Extraer info de entrega del objeto actividad (viene anidada en actividad.entrega)
  const entrega = actividad?.entrega;
  const tieneEntrega = !!entrega;
  const archivosEntrega = entrega?.archivos || [];

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onHide(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content radius-16 bg-base border-0 shadow-lg">

          {/* Header */}
          <div className="modal-header py-20 px-24 border-0"
            style={{ background: "linear-gradient(135deg, #f0fdf4, #dcfce7)", borderBottom: "3px solid #22c55e30" }}>
            <div className="d-flex align-items-center gap-12">
              <div className="w-40-px h-40-px bg-success-50 d-flex align-items-center justify-content-center radius-10">
                <Icon icon="mdi:check-decagram-outline" className="text-success-600" style={{ fontSize: "20px" }} />
              </div>
              <div>
                <h5 className="modal-title fw-semibold mb-2">Revisar entrega</h5>
                <span className="text-secondary-light text-sm">{actividad?.titulo}</span>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onHide} disabled={submitting} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body px-24 py-20">
              {/* Info de la entrega del alumno */}
              {tieneEntrega ? (
                <>
                  {/* Datos del alumno y fecha */}
                  <div className="row g-12 mb-16">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-8 p-12 radius-10" style={{ backgroundColor: "#f8fafc" }}>
                        <Icon icon="mdi:account-school" style={{ color: "#64748b", fontSize: "18px", flexShrink: 0 }} />
                        <div>
                          <span className="text-secondary-light text-xs d-block">Alumno</span>
                          <span className="text-sm fw-medium">{actividad?.alumno_nombre || "—"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-8 p-12 radius-10" style={{ backgroundColor: "#f8fafc" }}>
                        <Icon icon="mdi:clock-check-outline" style={{ color: "#64748b", fontSize: "18px", flexShrink: 0 }} />
                        <div>
                          <span className="text-secondary-light text-xs d-block">Entregado el</span>
                          <span className="text-sm fw-medium">
                            {entrega?.fecha_entrega_real
                              ? new Date(entrega.fecha_entrega_real).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Archivos de la entrega */}
                  {archivosEntrega.length > 0 && (
                    <div className="mb-16">
                      <span className="classroom-detail-label mb-8 d-block">Archivos entregados</span>
                      <div className="d-flex flex-column gap-6">
                        {archivosEntrega.map((archivo, idx) => {
                          const fileUrl = archivo.archivo || "";
                          const fileName = archivo.nombre_original || `Archivo ${idx + 1}`;
                          return (
                            <a key={idx} href={fileUrl} target="_blank" rel="noopener noreferrer"
                              className="classroom-file-item text-decoration-none d-flex align-items-center gap-10 p-10"
                              style={{ borderLeft: "3px solid #3b82f6" }}>
                              <Icon icon={getFileIcon(fileName)} style={{ color: "#3b82f6", fontSize: "20px", flexShrink: 0 }} />
                              <span className="text-sm text-truncate flex-grow-1">{fileName}</span>
                              <Icon icon="mdi:download" style={{ color: "#3b82f6", fontSize: "16px", flexShrink: 0 }} />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Comentario del alumno */}
                  {entrega?.comentario_alumno && (
                    <div className="mb-16">
                      <span className="classroom-detail-label mb-8 d-block">Comentario del alumno</span>
                      <div className="p-14 radius-10 d-flex gap-10" style={{ backgroundColor: "#eff6ff" }}>
                        <Icon icon="mdi:comment-text-outline" style={{ color: "#3b82f6", fontSize: "18px", flexShrink: 0, marginTop: "2px" }} />
                        <p className="text-sm mb-0" style={{ whiteSpace: "pre-wrap" }}>{entrega.comentario_alumno}</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16 mb-8">
                  <Icon icon="mdi:file-search-outline" style={{ fontSize: "36px", color: "#94a3b8" }} />
                  <p className="text-secondary-light text-sm mt-8 mb-0">
                    El alumno aún no ha subido archivos ni comentarios en su entrega.
                  </p>
                </div>
              )}

              <hr className="my-16" style={{ borderColor: "#e2e8f0" }} />

              {/* Acción de revisar */}
              <p className="text-secondary-light text-sm mb-16">
                Revisa la entrega del alumno. Puedes{" "}
                <strong style={{ color: "#059669" }}>validar</strong> si es correcta o{" "}
                <strong style={{ color: "#dc2626" }}>rechazar</strong> para que el alumno la reenvíe.
              </p>

              {/* Comentario del docente */}
              <div className="mb-12">
                <label className="form-label fw-medium text-secondary-light mb-8">
                  Comentario <span className="text-secondary-light fw-normal ms-4">(opcional)</span>
                </label>
                <textarea className="form-control radius-10"
                  placeholder="Ej: Buen trabajo. / Falta el PDF, reenvíalo."
                  value={comentario} onChange={(e) => setComentario(e.target.value)}
                  rows={3} style={{ resize: "vertical" }} />
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer py-16 px-24 border-0 d-flex justify-content-end gap-8">
              <button type="button" className="btn btn-outline-secondary radius-10 px-16"
                onClick={onHide} disabled={submitting}>Cancelar</button>
              <button type="button"
                className="btn btn-outline-danger d-flex align-items-center gap-8 radius-10 px-16"
                disabled={submitting}
                onClick={handleSubmit("rechazar")}>
                {submitting ? (<><span className="spinner-border spinner-border-sm" /><span>Procesando...</span></>)
                  : (<><Icon icon="mdi:close-circle-outline" style={{ fontSize: "18px" }} /><span>Rechazar</span></>)}
              </button>
              <button type="button"
                className="btn btn-success d-flex align-items-center gap-8 radius-10 px-20"
                disabled={submitting}
                onClick={handleSubmit("validar")}>
                {submitting ? (<><span className="spinner-border spinner-border-sm" /><span>Procesando...</span></>)
                  : (<><Icon icon="mdi:check-decagram" style={{ fontSize: "18px" }} /><span>Validar</span></>)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RevisionModal;
